import React, { useEffect, useContext, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Popup from "reactjs-popup"
import GalleryFlowObject from '../../model/GalleryFlowObject';
import GalleryAsset from '../../model/GalleryAsset';
import ImageAsset, { throwIfWrong } from '../../model/Assets';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { MachineContext } from '../fsm/useMachine';
import { addGalleryHelper } from './adding';
import { DELEGATE, CHANGE_GALLERY_ASSET } from '../fsm/types';
import GalleryIcon from "../icons/GalleryIcon"
import { XCircle } from "react-feather"

export const GalleryPicker: React.FC<{ showTitle: boolean }> = ({ showTitle }) => {
    const [current] = useContext(MachineContext)
    // Only use edit mode when in panel (!showTitle) AND a gallery is selected
    const isEditMode = !showTitle && current.context.currentObject?.type == "gallery"

    return (
        <Popup
            trigger={
                <div className={`div_styles ${isEditMode ? "gallery-edit-button" : ""}`} title={`${isEditMode ? "Edit" : "Add"} Gallery`}>
                    <label>
                        <GalleryIcon />
                    </label>
                    {showTitle && <span>Gallery</span>}
                </div>
            } modal closeOnDocumentClick>
            {(close: () => void) =>
                <React.Fragment>
                    {isEditMode ? (<GalleryEdit close={close} />) : (<GalleryPreview close={close} />)}
                </React.Fragment>
            }

        </Popup>
    )
}

type GalleryFile = { file: File; preview: string; caption: string }

export const GalleryPreview: React.FC<{ close: () => void }> = ({ close }) => {
    const [files, setFiles] = useState<GalleryFile[]>([]);
    const [current, send] = useContext(MachineContext)

    const onSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
        console.log("firing")
        e.preventDefault()
        let galleryImgs = GalleryAsset.default();
        files.forEach((file: GalleryFile) => {
            const fileAsFile = file.file as File
            galleryImgs.captionTextArray.push(file.caption)
            let imgAsset = ImageAsset.default({ __tag: "string", value: "" }, false, fileAsFile.name)
            imgAsset.externalURL = { __tag: "file", value: fileAsFile }
            galleryImgs.fullSizedImages.push(imgAsset)
            galleryImgs.thumbnailImages.push(imgAsset)
            galleryImgs.lowQualityImages.push(imgAsset)
        })
        addGalleryHelper(galleryImgs, current.context.flow.viewSize, send, current.context.index)
        close()
    }

    const { getRootProps, getInputProps } = useDropzone({
        accept: 'image/*',
        onDrop: (acceptedFiles: File[]) => {
            let newFiles: GalleryFile[] = acceptedFiles.map((file: any) => ({ file, preview: URL.createObjectURL(file), caption: "" }))
            setFiles(curr => [...curr, ...newFiles]);
        }
    });

    const SortableItem = SortableElement(({ value, idx }: { value: GalleryFile, idx: number }) => (
        <div className="thumb">
            <div className="thumb-inner">
                <img
                    src={value.preview}
                    className="thumb-img"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                />
                <input type="text" placeholder="Caption this photo" defaultValue={value.caption} onChange={(e) => {
                    files[idx].caption = e.currentTarget.value
                }} />
            </div>
        </div>
    ));

    const List = SortableContainer(({ items }: { items: GalleryFile[] }) => {
        console.log('[Gallery] Rendering list with items:', items.map((v, i) => ({ index: i, name: v.file.name, hasPreview: !!v.preview })))
        return <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row' }}>
            {items.map((value: GalleryFile, index: number) => (
                <SortableItem
                    key={`${value.file.name}-${value.file.size}-${index}`}
                    index={index}
                    value={value}
                    idx={index}
                />
            ))}
        </div>;
    })

    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
        console.log('[Gallery] onSortEnd called', { oldIndex, newIndex, filesCount: files.length })
        console.log('[Gallery] Files before:', files.map((f, i) => ({ index: i, name: f.file.name, preview: f.preview.substring(0, 50) })))

        const newFiles = arrayMove([...files], oldIndex, newIndex)

        console.log('[Gallery] Files after arrayMove:', newFiles.map((f, i) => ({ index: i, name: f.file.name, preview: f.preview.substring(0, 50) })))

        setFiles(newFiles)

        // Verify state update on next tick
        setTimeout(() => {
            console.log('[Gallery] Files in state after setFiles:', files.map((f, i) => ({ index: i, name: f.file.name })))
        }, 0)
    }

    useEffect(() => {
        // Only revoke URLs on unmount, not on every state change
        return () => {
            console.log('[Gallery] Component unmounting, revoking blob URLs')
            files.forEach(file => URL.revokeObjectURL(file.preview));
        }
    }, []); // Empty dependency array = only run on mount/unmount

    return (
        <section className="thumbs-section">
            <div className="gallery-modal-header">
                <div>
                    <h3 className="gallery-modal-title">Create Image Gallery</h3>
                    <p className="gallery-modal-description">Add multiple images that viewers can swipe through. Drag to reorder.</p>
                </div>
                <XCircle className="feather close-icon" onClick={close} style={{ cursor: 'pointer', flexShrink: 0 }} />
            </div>
            <div {...getRootProps({ className: files.length === 0 ? "dropzone-empty" : 'dropzone' })}>
                <input {...getInputProps()} />
                <p>{files.length === 0 ? 'Drag and drop images here, or click to browse' : 'Add more images...'}</p>
            </div>
            <aside className="thumbs-container">
                <List items={files} onSortEnd={onSortEnd} axis="xy" distance={5} helperClass="dragging-thumb" useDragHandle={false} />
            </aside>
            <button onClick={onSave} disabled={files.length === 0}>Add Gallery to Flow</button>
        </section>
    );
}

type ExistingGalleryFile = GalleryFile & { isNew: boolean } | { file: ImageAsset, preview: string; caption: string; isNew: boolean }

export const GalleryEdit: React.FC<{ close: () => void }> = ({ close }) => {
    const [current, send] = useContext(MachineContext)
    let currentObject = current.context.currentObject as GalleryFlowObject
    const [files, setFiles] = useState<ExistingGalleryFile[]>([]);
    const [galleryObject, setGalleryObject] = useState<GalleryFlowObject>(currentObject!)

    React.useEffect(() => {
        setFiles(currentObject.galleryAssets!.fullSizedImages.map((img: ImageAsset, idx: number) => {
            // Handle different URL types: string URLs (CDN), File objects, or tagged objects
            let previewUrl: string;
            if (typeof img.externalURL === "string") {
                // Plain string URL (after upload, stored as CDN URL)
                previewUrl = img.externalURL;
            } else if (img.externalURL.__tag === "string") {
                // Tagged string (before upload, base64 or temporary URL)
                previewUrl = img.externalURL.value as string;
            } else if (img.externalURL.__tag === "file") {
                // File object (newly added, not yet uploaded)
                previewUrl = URL.createObjectURL(img.externalURL.value as File);
            } else {
                console.warn("[GalleryEdit] Unknown externalURL type:", img.externalURL);
                previewUrl = "";
            }
            return { file: img, preview: previewUrl, caption: currentObject.galleryAssets!.captionTextArray[idx], isNew: false }
        }))
    }, [])

    const onSave = async () => {
        let galleryImgs = galleryObject.galleryAssets!
        files.forEach((file: ExistingGalleryFile) => {
            if (file.isNew) {
                galleryImgs.captionTextArray.push(file.caption)
                let imgAsset = ImageAsset.default({ __tag: "file", value: file.file as File }, false, (file.file as File).name)
                galleryImgs.fullSizedImages.push(imgAsset)
                galleryImgs.thumbnailImages.push(imgAsset)
                galleryImgs.lowQualityImages.push(imgAsset)
            }
        })
        send({ type: DELEGATE, payload: { type: CHANGE_GALLERY_ASSET, gallery: galleryImgs }, id: currentObject.id })
        close()
    }

    const { getRootProps, getInputProps } = useDropzone({
        accept: 'image/*',
        onDrop: (acceptedFiles: File[]) => {
            let newFiles: ExistingGalleryFile[] = acceptedFiles.map((file: any) => ({ file, preview: URL.createObjectURL(file), caption: "", isNew: true }))
            setFiles(curr => [...curr, ...newFiles]);
        }
    });

    const SortableItem = SortableElement(({ value, idx }: { value: ExistingGalleryFile, idx: number }) => (
        <div className="thumb">
            <div className="thumb-inner">
                <img
                    src={value.preview}
                    className="thumb-img"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                />
                <input type="text" placeholder="Caption this photo" defaultValue={value.caption} onChange={(e) => {
                    files[idx].caption = e.currentTarget.value
                    galleryObject.galleryAssets!.captionTextArray[idx] = e.currentTarget.value
                }} />
            </div>
        </div>));

    useEffect(() => {
        // Only revoke URLs on unmount, not on every state change
        return () => {
            console.log('[GalleryEdit] Component unmounting, revoking blob URLs')
            files.forEach(file => URL.revokeObjectURL(file.preview));
        }
    }, []); // Empty dependency array = only run on mount/unmount

    const List = SortableContainer(({ items }: { items: ExistingGalleryFile[] }) => {
        return <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row' }}>
            {items.map((value: ExistingGalleryFile, index: number) => {
                const keyId = (value.file as File).name || (value.file as ImageAsset).id;
                return <SortableItem key={`${keyId}-${index}`} index={index} value={value} idx={index} />
            })}
        </div>;
    })

    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
        console.log('[GalleryEdit] onSortEnd called', { oldIndex, newIndex, filesCount: files.length })
        console.log('[GalleryEdit] Files before:', files.map((f, i) => ({ index: i, preview: f.preview.substring(0, 50) })))

        const newFiles = arrayMove([...files], oldIndex, newIndex)
        console.log('[GalleryEdit] Files after arrayMove:', newFiles.map((f, i) => ({ index: i, preview: f.preview.substring(0, 50) })))

        setFiles(newFiles)

        let newGallery = {...galleryObject}
        newGallery.galleryAssets = {...galleryObject.galleryAssets!}
        newGallery.galleryAssets.captionTextArray = arrayMove([...galleryObject.galleryAssets!.captionTextArray], oldIndex, newIndex)
        newGallery.galleryAssets.lowQualityImages = arrayMove([...galleryObject.galleryAssets!.lowQualityImages], oldIndex, newIndex)
        newGallery.galleryAssets.thumbnailImages = arrayMove([...galleryObject.galleryAssets!.thumbnailImages], oldIndex, newIndex)
        newGallery.galleryAssets.fullSizedImages = arrayMove([...galleryObject.galleryAssets!.fullSizedImages], oldIndex, newIndex)
        setGalleryObject(newGallery)
    }

    return (
        <section className="thumbs-section">
            <div className="gallery-modal-header">
                <div>
                    <h3 className="gallery-modal-title">Edit Gallery</h3>
                    <p className="gallery-modal-description">Add more images or drag to reorder</p>
                </div>
                <XCircle className="feather close-icon" onClick={close} style={{ cursor: 'pointer', flexShrink: 0 }} />
            </div>
            <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <p>Add more images to this gallery</p>
            </div>
            <aside className="thumbs-container">
                <List items={files} onSortEnd={onSortEnd} axis="xy" distance={5} helperClass="dragging-thumb" useDragHandle={false} />
            </aside>
            <button onClick={onSave}>Save Changes</button>
        </section>
    );
}

function arrayMoveMutate<T>(array: T[], from: number, to: number) {
    const startIndex = from < 0 ? array.length + from : from;

    if (startIndex >= 0 && startIndex < array.length) {
        const endIndex = to < 0 ? array.length + to : to;

        const [item] = array.splice(from, 1);
        array.splice(endIndex, 0, item);
    }
};

function arrayMove<T>(array: T[], from: number, to: number): T[] {
    array = [...array];
    arrayMoveMutate(array, from, to);
    return array;
};
